import { styled } from '@mui/material/styles'
import { ReactElement, useEffect, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { Router } from 'next/router'
import { Header } from '@/components/header/Header'
import { SPACING_VARIABLES } from '@/theme/themeOptions/vars'
import { useAuthContext } from '@/context/auth'
import { Banner } from '@/components/header/Banner'

interface LayoutProps {
  children: React.ReactNode
}

interface LayoutStyledProps {
  isBannerVisible?: boolean
}

const LayoutStyled = styled('main', {
  shouldForwardProp: (prop) => prop !== 'isBannerVisible'
})<LayoutStyledProps>(({ theme, isBannerVisible }) => ({
  background: theme.palette.grey[50],
  MozOsxFontSmoothing: 'grayscale',
  WebkitFontSmoothing: 'antialiased',
  scrollBehavior: 'smooth',
  paddingTop: !isBannerVisible
    ? SPACING_VARIABLES.APP_HEADER_HEIGHT
    : SPACING_VARIABLES.APP_HEADER_HEIGHT + SPACING_VARIABLES.BANNER_HEIGHT,
  [theme.breakpoints.down('sm')]: {
    paddingTop: !isBannerVisible
      ? SPACING_VARIABLES.MOBILE_APP_HEADER_HEIGHT
      : SPACING_VARIABLES.MOBILE_APP_HEADER_HEIGHT + SPACING_VARIABLES.BANNER_HEIGHT
  },
  transition: 'all .25s ease-in-out',
  minHeight: '100vh'
}))

const LoadingStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: `calc(100vh - ${SPACING_VARIABLES.APP_HEADER_HEIGHT}px)`,
  [theme.breakpoints.down('sm')]: {
    height: `calc(100vh - ${SPACING_VARIABLES.MOBILE_APP_HEADER_HEIGHT}px)`
  }
}))

export const LoadingComponent = (): ReactElement => (
  <LoadingStyled>
    <CircularProgress />
  </LoadingStyled>
)

const Layout = ({ children }: LayoutProps): ReactElement => {
  const { isBannerDisplayed } = useAuthContext()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    const start = (): void => setIsLoading(true)
    const end = (): void => setIsLoading(false)

    Router.events.on('routeChangeStart', start)
    Router.events.on('routeChangeComplete', end)
    Router.events.on('routeChangeError', end)

    return (): void => {
      Router.events.off('routeChangeStart', start)
      Router.events.off('routeChangeComplete', end)
      Router.events.off('routeChangeError', end)
    }
  }, [])

  return (
    <LayoutStyled isBannerVisible={isBannerDisplayed}>
      <Header />
      <Banner />
      {isLoading ? <LoadingComponent /> : children}
    </LayoutStyled>
  )
}

export default Layout
